// Herkese açık hesap silme sayfası — Google Play "Data deletion" şartı.
// /hesap-sil ve /account-deletion aynı bileşene çıkar; Play Console'a İngilizce
// olan URL girilir. Bkz. docs/hesap-silme-sayfasi.md
//
// Sayfa bilerek oturum GEREKTİRMEZ: kullanıcı uygulamayı kaldırmış olsa bile
// hesabını silebilmeli. Kendi içinde giriş yapar, alınan access token yalnızca
// bu bileşenin state'inde (bellekte) tutulur — localStorage/cookie'ye yazılmaz.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserSlash,
  faTrashCan,
  faCircleCheck,
  faTriangleExclamation,
  faBoxArchive,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { TextField, PasswordField, ErrorBanner } from '../auth/fields';
import { looksLikeEmail } from '../../utils/validators';
import { loginForDeletion, deleteAccountWithToken } from './deleteAccountApi';
import '../auth/auth.css'; // .auth-form / .field / .auth-submit / .auth-error
import './deleteAccount.css';

const SUPPORT_EMAIL = 'info@utkanesim.com';

// 5. bölümdeki listeler giriş yapılmadan görünür olmalı — Google inceleyicisi
// sayfaya girip okuyor, accordion arkasına saklanmıyor.
const DELETED_KEYS = [
  'del.deleted.identity',
  'del.deleted.credentials',
  'del.deleted.codes',
  'del.deleted.esim',
];
const WARN_KEYS = ['del.warn.irreversible', 'del.warn.esim', 'del.warn.rejoin'];

const DeleteAccount = () => {
  const { t } = useTranslation();
  const { isAuthenticated, email: sessionEmail, logout } = useAuth();

  // 'login' → 'confirm' → 'done'
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retained, setRetained] = useState(null);

  // Token state'te değil ref'te: render'a sızmasın, React DevTools'ta props/state
  // ağacında görünmesin, cancel'da senkron temizlenebilsin.
  const tokenRef = useRef(null);

  useEffect(() => {
    document.title = `${t('del.title')} — Utkan eSIM`;
  }, [t]);

  // Bileşen ekrandan kalkarken token'ı bellekte bırakma.
  useEffect(() => () => {
    tokenRef.current = null;
  }, []);

  // HTTP durumunu ekran metnine çevirir; sözleşme durum kodudur (bölüm 4).
  const loginError = (err) => {
    if (err.status === null) return t('del.error.network');
    if (err.status === 401) return t('del.error.credentials');
    if (err.status === 403) return t('del.error.suspended');
    return t('del.error.generic');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    const clean = email.trim();
    if (!looksLikeEmail(clean) || !password) {
      setError(t('del.error.required'));
      return;
    }

    setError(null);
    setLoading(true);
    try {
      tokenRef.current = await loginForDeletion({ email: clean, password });
      setEmail(clean);
      setPassword(''); // şifreyi DOM'da/state'te bırakma
      setConfirmEmail('');
      setStep('confirm');
    } catch (err) {
      setError(loginError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    tokenRef.current = null;
    setStep('login');
    setPassword('');
    setConfirmEmail('');
    setError(null);
  };

  const handleDelete = async () => {
    if (loading || !tokenRef.current) return;

    setError(null);
    setLoading(true);
    try {
      const data = await deleteAccountWithToken(tokenRef.current);
      tokenRef.current = null;

      // Aynı hesapla bu tarayıcıda oturum açıksa o oturum artık ölü: token'ları
      // temizle. Yönlendirme yapma — kullanıcı sonuç ekranını görmeli.
      if (
        isAuthenticated &&
        sessionEmail &&
        sessionEmail.toLowerCase() === email.toLowerCase()
      ) {
        logout({ redirect: false });
      }

      setRetained(data?.retainedOrders ?? null);
      setStep('done');
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        // Token süresi doldu ya da oturum eşleşmedi → baştan giriş.
        tokenRef.current = null;
        setStep('login');
        setError(t('del.error.session'));
      } else if (err.status === null) {
        setError(t('del.error.network'));
      } else {
        setError(t('del.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmMatches =
    confirmEmail.trim().toLowerCase() === email.trim().toLowerCase();

  return (
    <div className="del">
      <StoreHeader />

      <main className="del-inner">
        <header className="del-head">
          <span className="del-icon">
            <FontAwesomeIcon icon={faUserSlash} />
          </span>
          <p className="del-eyebrow">{t('del.eyebrow')}</p>
          <h1>{t('del.title')}</h1>
          <p className="del-intro">{t('del.intro')}</p>
        </header>

        <div className="del-grid">
          {/* Sol sütun: ne siliniyor, ne saklanıyor, uyarılar — giriş
              yapılmadan görünür. */}
          <div className="del-info">
            <section className="del-block">
              <h2>
                <FontAwesomeIcon icon={faTrashCan} />
                {t('del.deleted.title')}
              </h2>
              <ul className="del-list">
                {DELETED_KEYS.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            </section>

            <section className="del-block">
              <h2>
                <FontAwesomeIcon icon={faBoxArchive} />
                {t('del.kept.title')}
              </h2>
              <ul className="del-list">
                <li>{t('del.kept.orders')}</li>
                <li>{t('del.kept.period')}</li>
              </ul>
            </section>

            <section className="del-block del-block--warn">
              <h2>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {t('del.warn.title')}
              </h2>
              <ul className="del-list">
                {WARN_KEYS.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            </section>

            <p className="del-support">
              {t('del.support')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          </div>

          {/* Sağ sütun: akış kartı */}
          <div className="del-card">
            {step === 'login' && (
              <>
                <h2 className="del-card-title">{t('del.login.title')}</h2>
                <p className="del-card-sub">{t('del.login.subtitle')}</p>

                <form className="auth-form" onSubmit={handleLogin} noValidate>
                  <ErrorBanner message={error} />

                  <TextField
                    id="del-email"
                    label={t('auth.field.email')}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={t('auth.field.email.ph')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />

                  <PasswordField
                    id="del-password"
                    label={t('auth.field.password')}
                    autoComplete="current-password"
                    placeholder={t('auth.field.password.ph')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />

                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading && <span className="auth-spinner" />}
                    {loading ? t('del.login.loading') : t('del.login.action')}
                  </button>
                </form>

                <p className="del-card-foot">
                  <Link to="/forgot-password">{t('del.forgot')}</Link>
                </p>
              </>
            )}

            {step === 'confirm' && (
              <>
                <h2 className="del-card-title">{t('del.confirm.title')}</h2>
                <p className="del-card-sub">
                  {t('del.confirm.subtitle', { email })}
                </p>

                <ErrorBanner message={error} />

                <div className="del-danger">{t('del.warn.irreversible')}</div>

                <div className="auth-form">
                  <TextField
                    id="del-confirm-email"
                    label={t('del.confirm.field')}
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    placeholder={t('auth.field.email.ph')}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    autoFocus
                  />

                  <button
                    type="button"
                    className="del-submit"
                    onClick={handleDelete}
                    disabled={!confirmMatches || loading}
                  >
                    {loading && <span className="auth-spinner" />}
                    {loading ? t('del.confirm.deleting') : t('del.confirm.action')}
                  </button>

                  <button
                    type="button"
                    className="del-cancel"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('del.confirm.cancel')}
                  </button>
                </div>
              </>
            )}

            {step === 'done' && (
              <div className="del-done">
                <FontAwesomeIcon icon={faCircleCheck} className="del-done-icon" />
                <h2 className="del-card-title">{t('del.done.title')}</h2>
                <p className="del-card-sub">
                  {retained
                    ? t('del.done.retained', { n: retained })
                    : t('del.done.body')}
                </p>
                <Link to="/" className="del-home">
                  {t('del.done.home')}
                  <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DeleteAccount;
